import { HTMLAttributes, forwardRef } from 'react';
import './Spinner.css';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Accessible label for screen readers */
  label?: string;
}

/**
 * Spinner component for loading states.
 *
 * @example
 * ```tsx
 * <Spinner size="md" label="Loading data..." />
 * ```
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', label = 'Loading', className = '', ...props }, ref) => {
    const classNames = ['ui-spinner', `ui-spinner--${size}`, className]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        role="status"
        aria-label={label}
        className={classNames}
        {...props}
      >
        <svg
          className="ui-spinner__svg"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="ui-spinner__track"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
          />
          <path
            className="ui-spinner__head"
            d="M22 12a10 10 0 00-10-10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        <span className="ui-spinner__label">{label}</span>
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';
